import { useEffect, useRef, useState } from 'react';

const ROOT_MARGIN = '300px'; // mount a bit before the block reaches the viewport

/**
 * True once the ref'd element nears the viewport — used to lazy-mount widget
 * iframes so N connected agents don't spin up N iframes + sign-bridges on the
 * home scroll. Latches on: a mounted widget isn't torn down on scroll-away.
 * ponytail: latch-on, fixed 300px margin (the only caller); add options only
 * when a second caller genuinely needs them. Add unmount-off-screen only if
 * memory becomes an issue.
 */
export function useInViewport<T extends Element>(): [React.RefObject<T | null>, boolean] {
  const ref = useRef<T>(null);
  const [inView, setInView] = useState(typeof IntersectionObserver === 'undefined');

  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return;
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) {
        setInView(true);
        io.disconnect();
      }
    }, { rootMargin: ROOT_MARGIN });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return [ref, inView];
}
